import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback } from "react";
import { FormProvider, useForm } from "react-hook-form";
import * as yup from "yup";
import shallow from "zustand/shallow";
import { SOLE_PROPRIETORSHIP } from "../../../constants/legalEntityTypes";
import { PLACEHOLDER_NONE } from "../../../constants/selectionValues";
import useStore from "../../../store/store";
import useFocusHeading from "../../../utils/hooks/useFocusHeading";
import usePageTitle from "../../../utils/hooks/usePageTitle";
import usePreventNavigation from "../../../utils/hooks/usePreventNavigation";
import DisabledField from "../../shared/DisabledField";
import Heading from "../../shared/Heading";
import { CenteredRow, Col, Grid, Row } from "../../shared/Layout";
import LoadingSpinnerPage from "../../shared/LoadingSpinnerPage";
import NavigationButtons from "../../shared/NavigationButtons";
import Page from "../../shared/Page";
import SectionHeader from "../../shared/SectionHeader";
import TextInput from "../../shared/TextInput";
import selectFirstFieldWithError from "../../../utils/selectFirstFieldWithError";
import { useNavigateUnlessReview } from "../../../utils/reviewContext";
import useSubmitWithErrorHandling from "../../../utils/hooks/useSubmitWithErrorHandling";
import useRouting from "../../../utils/hooks/useRouting";
import AccountPurposeFields, {
  accountPurposeSchema,
  useDefaultAccountPurpose,
} from "../../shared/AccountPurposeFields";
import doingBusinessNameSchema from "../../../utils/doingBusinessNameSchema";
import AnnualRevenueRangeSelect, { annualRevenueRangeSchema } from "../../shared/AnnualRevenueSelect";
import CharitableOrganizationSection, {
  conditionalCharitableOrganizationSchema,
  useDefaultCharitableOrganizationInfo,
} from "../../shared/CharitableOrganizationSection";
import BusinessOutsideUSSection, {
  conditionalBusinessOutsideUSSchema,
  useDefaultOutsideUSInfo,
} from "../../shared/BusinessOutsideUSSection";
import BusinessIndustryCombobox, {
  businessIndustrySchema,
  useIsIndustryListLoaded,
} from "../../shared/BusinessIndustryCombobox";
import { useInputId } from "../../../utils/hooks/usePageScopedId";

const detailSchema = yup.object().shape({
  doingBusinessName: doingBusinessNameSchema,
  businessIndustry: businessIndustrySchema,
  annualRevenueRange: annualRevenueRangeSchema,
  accountPurpose: accountPurposeSchema,
  ...conditionalBusinessOutsideUSSchema,
  ...conditionalCharitableOrganizationSchema,
});

const DoingBusinessName = ({ register, errors }) => (
  <TextInput
    id={useInputId("doingBusinessName")}
    label="Doing Business As (DBA) Name (If Applicable)"
    helper="Enter your public-facing name. This will appear on any checks you order."
    error={errors?.doingBusinessName?.message}
    maxLength={36}
    {...register("doingBusinessName")}
  />
);

const BusinessDetails = () => {
  usePageTitle("Business Details");
  usePreventNavigation();
  const headingRef = useFocusHeading();
  const [
    isSoleProp,
    legalBusinessName,
    storedDoingBusinessAsName,
    storedIndustry,
    storedAnnualRevenueRange,
    industryCodeLookup,
    submitBusinessDetails,
  ] = useStore(
    state => [
      state.businessLegalEntityType === SOLE_PROPRIETORSHIP,
      `${state.applicantFirstName} ${state.applicantLastName}`,
      state.businessDoingBusinessAsName,
      state.businessIndustryDescription,
      state.businessAnnualRevenueRange,
      state.industryContent.industryCodeLookup,
      state.pageSubmit.submitBusinessDetails,
    ],
    shallow
  );

  const accountPurposeDefault = useDefaultAccountPurpose();
  const charitableOrgDefault = useDefaultCharitableOrganizationInfo();
  const outsideUSDefault = useDefaultOutsideUSInfo();

  const formMethods = useForm({
    resolver: yupResolver(detailSchema),
    context: { industryCodeLookup },
    shouldFocusError: false,
    defaultValues: {
      doingBusinessName: storedDoingBusinessAsName || "",
      businessIndustry: storedIndustry || "",
      annualRevenueRange: storedAnnualRevenueRange || PLACEHOLDER_NONE,
      accountPurpose: accountPurposeDefault,
      ...outsideUSDefault,
      ...charitableOrgDefault,
    },
  });

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = formMethods;

  const navigate = useNavigateUnlessReview();
  const [backRoute, nextRoute] = useRouting();

  const onSubmit = useCallback(
    async ({
      doingBusinessName,
      businessIndustry,
      annualRevenueRange,
      accountPurpose,
      isBusinessHeadquarteredOutsideUS,
      isCharitableOrganization,
      outsideUSInfo,
      charitableOrganization,
    }) => {
      const naicsCode = industryCodeLookup.get(businessIndustry);
      await submitBusinessDetails({
        doingBusinessName,
        businessIndustry,
        naicsCode,
        annualRevenueRange,
        accountPurpose,
        isBusinessHeadquarteredOutsideUS,
        isCharitableOrganization,
        outsideUSInfo,
        charitableOrganization,
      });
      navigate(nextRoute);
    },
    [submitBusinessDetails, industryCodeLookup, navigate, nextRoute]
  );
  const submitWithErrorHandling = useSubmitWithErrorHandling(onSubmit);

  const industryListLoaded = useIsIndustryListLoaded();
  if (!industryListLoaded) {
    return <LoadingSpinnerPage />;
  }

  return (
    <Page>
      <Grid ref={headingRef}>
        <Heading
          step="SECTION 2 OF 4"
          mainHeading="Tell us about your business"
          subHeading="To create your application, we need some details about your business."
        />
        <CenteredRow>
          <Col lg={8} md={8} sm={4}>
            <SectionHeader title="Business details" />
          </Col>
        </CenteredRow>
        {isSoleProp && (
          <Row>
            <Col lg={5} md={4} offset={{ lg: 2 }} sm={4}>
              <DisabledField
                helper="Since your company is a Sole Proprietorship, we filled this in with the legal first and last name you entered earlier"
                label="Legal Business Name"
                value={legalBusinessName}
              />
            </Col>
          </Row>
        )}
      </Grid>
      <form onSubmit={handleSubmit(submitWithErrorHandling, selectFirstFieldWithError)}>
        <Grid>
          <Row>
            <Col lg={5} md={4} offset={{ lg: 2 }} sm={4}>
              <DoingBusinessName {...{ register, errors }} />
            </Col>
          </Row>
          <CenteredRow>
            <Col lg={8} md={8} sm={4}>
              <BusinessIndustryCombobox control={control} />
            </Col>
          </CenteredRow>
          <Row>
            <Col lg={4} md={3} offset={{ lg: 2 }} sm={4}>
              <AnnualRevenueRangeSelect
                label="Annual Business Revenue"
                helper="This refers to the gross income in 1 year"
                error={errors?.annualRevenueRange?.message}
                {...register("annualRevenueRange")}
              />
            </Col>
          </Row>
        </Grid>
        <FormProvider {...formMethods}>
          <Grid>
            <AccountPurposeFields />
          </Grid>
          <BusinessOutsideUSSection />
          <CharitableOrganizationSection />
        </FormProvider>
        <NavigationButtons backRoute={backRoute} nextLoading={isSubmitting} />
      </form>
    </Page>
  );
};

export default BusinessDetails;
